// Blackbody colour for a temperature: Planck's law integrated against
// the CIE 1931 2° observer (analytic multi-lobe fit, Wyman, Sloan and
// Shirley 2013), XYZ to linear sRGB, normalised so the brightest
// channel is 1. sRGB white is D65, so the Sun (5772 K) comes out a
// touch warm, which is what a camera balanced for daylight records.
// Pure JS so procgen and node scripts can use it without three.js.

function g(x, mu, s1, s2) {
  const t = (x - mu) / (x < mu ? s1 : s2);
  return Math.exp(-0.5 * t * t);
}

const cache = new Map();

export function blackbodyRgb(tempK) {
  const T = Math.max(500, Math.min(60000, tempK));
  const key = Math.round(T / 10);
  if (cache.has(key)) return cache.get(key).slice();
  let X = 0, Y = 0, Z = 0;
  for (let nm = 380; nm <= 780; nm += 5) {
    const l = nm * 1e-9;
    const planck = 1 / (Math.pow(l, 5) * (Math.exp(1.4388e-2 / (l * T)) - 1));
    const xb = 1.056 * g(nm, 599.8, 37.9, 31.0) + 0.362 * g(nm, 442.0, 16.0, 26.7) - 0.065 * g(nm, 501.1, 20.4, 26.2);
    const yb = 0.821 * g(nm, 568.8, 46.9, 40.5) + 0.286 * g(nm, 530.9, 16.3, 31.1);
    const zb = 1.217 * g(nm, 437.0, 11.8, 36.0) + 0.681 * g(nm, 459.0, 26.0, 13.8);
    X += planck * xb; Y += planck * yb; Z += planck * zb;
  }
  let r = 3.2406 * X - 1.5372 * Y - 0.4986 * Z;
  let gg = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
  let b = 0.0557 * X - 0.2040 * Y + 1.0570 * Z;
  r = Math.max(0, r); gg = Math.max(0, gg); b = Math.max(0, b);
  const m = Math.max(r, gg, b) || 1;
  const out = [r / m, gg / m, b / m];
  cache.set(key, out);
  return out.slice();
}

// Linear → sRGB-encoded hex, for display fields (glowColor, colors[]).
export function linearRgbToHex(rgb) {
  const enc = (v) => {
    const c = Math.max(0, Math.min(1, v));
    const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.round(s * 255).toString(16).padStart(2, '0');
  };
  return `#${enc(rgb[0])}${enc(rgb[1])}${enc(rgb[2])}`;
}

// The light a planet sees from its star: the blackbody hue, eased a
// little toward white (the eye and camera adapt), max channel 1.
export function starLightRgb(tempK, adapt = 0.42) {
  const bb = blackbodyRgb(tempK);
  const mixed = bb.map((v) => v * (1 - adapt) + adapt);
  const m = Math.max(...mixed);
  return mixed.map((v) => v / m);
}
